import PropTypes from 'prop-types'
import _ from 'lodash'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { displayWithCurrency, totalsByCurrency } from '../../../../utility/currency'
import { selectConsumerPlan, selectQuote } from '../../../../reducers/programRegistrationReducer'

import './OrderSummary.scss'
import AvatarPlaceholder from '../../../../assets/images/mock/StockProfilePic.png'

const SummaryRow = ({ pill, title, subtitle, price, priceNote, taxNote, paymentNote }) => (
  <div className='d-flex justify-content-between order-summary-row'>
    <div>
      {!!pill && <span className='row-pill'>{pill}</span>}
      <p className='mb-0 row-title'>{title}</p>
      <p className='mb-0 row-subtitle'>{subtitle}</p>
    </div>
    {!!price && (
      <span className='row-price'>
        {price}
        {!!priceNote && <span className='row-price-note'>{priceNote}</span>}
        {!!taxNote && <span className='row-price-note'>{taxNote}</span>}
        {!!paymentNote && <span className='row-price-note'>{paymentNote}</span>}
      </span>
    )}
  </div>
)

const taxNoteFor = (line, t) => {
  if (line.tax === null) {
    return t('programs.registration.summary.taxPending')
  }

  if (!line.tax) {
    return null
  }

  return line.taxInclusive
    ? t('programs.registration.summary.taxIncluded')
    : t('programs.registration.summary.taxAdded', { amount: displayWithCurrency(line.tax, line.currency) })
}

const QuotedOrderRows = ({ quote, paid }) => {
  const { t } = useTranslation()
  const membershipIncluded = !!quote.membership?.covered
  const membershipUpgrade = quote.membership?.action === 'upgrade'
  const totals = totalsByCurrency(quote.pricing.lines.filter(line => line.paymentMethod === 'card'))

  return (
    <>
      {quote.pricing.lines.map(line => (
        line.key === 'membership'
          ? (
            <SummaryRow
              key={line.key}
              pill={paid ? null : t('programs.registration.summary.required')}
              title={t('programs.registration.summary.membership')}
              subtitle={t(`programs.registration.membershipPlan.tiers.${line.plan.tier}`)}
              price={displayWithCurrency(line.amount, line.currency)}
              priceNote={membershipUpgrade ? null : t('programs.registration.summary.perYear')}
              taxNote={taxNoteFor(line, t)}
            />
          )
          : (
            <SummaryRow
              key={line.key}
              title={t('programs.landing.actionsCard.programCoachingFee')}
              subtitle={line.packageName || t('programs.landing.actionsCard.pricingVariesByPackage')}
              price={displayWithCurrency(line.amount, line.currency)}
              taxNote={taxNoteFor(line, t)}
              paymentNote={line.paymentMethod === 'card' ? null : t('programs.registration.summary.paidOffline')}
            />
          )
      ))}
      {membershipIncluded && (
        <SummaryRow
          title={t('programs.registration.summary.membership')}
          subtitle={t('programs.registration.summary.membershipIncluded')}
        />
      )}
      {totals.map(total => (
        <div key={total.currency.code} className='d-flex justify-content-between align-items-center order-summary-total'>
          <span className='total-label'>
            {totals.length === 1
              ? t('programs.registration.summary.total')
              : t('programs.registration.summary.totalCurrency', { currency: total.currency.code.toUpperCase() })}
          </span>
          <span className='total-price'>{displayWithCurrency(total.amount, total.currency)}</span>
        </div>
      ))}
    </>
  )
}

const OrderSummary = () => {
  const course = useSelector(state => state.programPresentation.course)
  const {
    status,
    selectedFamilyMember,
    selectedPackage,
    selectedPaymentMethod,
    acceptedQuote,
  } = useSelector(state => state.programRegistration)
  const quote = useSelector(selectQuote)
  const quotedOrder = quote?.pricing ? quote : acceptedQuote
  const consumerPlan = useSelector(selectConsumerPlan)
  const { t } = useTranslation()

  const membershipToChoose = consumerPlan && status !== 'finished'

  const hasPlayer = !_.isEmpty(selectedFamilyMember)
  const hasPackage = !_.isEmpty(selectedPackage)
  const playerName = `${selectedFamilyMember?.firstName} ${selectedFamilyMember?.lastName}`
  const price = hasPackage ? displayWithCurrency(selectedPackage.price, course?.community?.currency) : '—'
  const paidOffline = hasPackage && selectedPaymentMethod === 'cash'

  return (
    <div className='order-summary'>
      <p className='order-summary-label'>{t('programs.registration.summary.orderSummary')}</p>
      <div className='d-flex align-items-center order-summary-player'>
        <div className='medium-round-image-wrapper'>
          <img
            className='round-image'
            src={hasPlayer && selectedFamilyMember.avatar?.fileUrl ? selectedFamilyMember.avatar.fileUrl : AvatarPlaceholder}
            alt={t('programs.registration.selectedFamilyMemberAlt')}
          />
        </div>
        <span className='ml-2 order-summary-player-name' title={hasPlayer ? playerName : null}>
          {hasPlayer ? playerName : t('programs.registration.summary.notSelectedYet')}
        </span>
      </div>
      {quotedOrder ? (
        <QuotedOrderRows quote={quotedOrder} paid={status === 'finished'} />
      ) : (
        <>
          <SummaryRow
            title={t('programs.landing.actionsCard.programCoachingFee')}
            subtitle={hasPackage ? selectedPackage.name : t('programs.landing.actionsCard.pricingVariesByPackage')}
            price={price}
            paymentNote={paidOffline ? t('programs.registration.summary.paidOffline') : null}
          />
          {membershipToChoose && (
            <SummaryRow
              pill={t('programs.registration.summary.required')}
              title={t('programs.registration.summary.membership')}
              subtitle={t('programs.registration.summary.notSelectedYet')}
              price='—'
            />
          )}
          {(membershipToChoose || !paidOffline) && (
            <div className='d-flex justify-content-between align-items-center order-summary-total'>
              <span className='total-label'>{t('programs.registration.summary.total')}</span>
              <span className='total-price'>{membershipToChoose ? '—' : price}</span>
            </div>
          )}
        </>
      )}
    </div>
  )
}

SummaryRow.propTypes = {
  pill: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  price: PropTypes.string,
  priceNote: PropTypes.string,
  taxNote: PropTypes.string,
  paymentNote: PropTypes.string,
}

QuotedOrderRows.propTypes = {
  quote: PropTypes.object.isRequired,
  paid: PropTypes.bool,
}

export default OrderSummary
